import $ from 'jquery';
import Vapi from '@vapi-ai/web';
import { releasePreloadedVapiWebRtc } from '../../helpers/preloadVapiWebRtc';
import { createVapiRCall } from '../../helpers/vapiRCall';
import { requestAiInteractionConsent } from '../engine/helpers/aiInteractionConsent';
import { escapeHtml } from '../../helpers/aiToken';
import { VapiChair } from '../vapi-UI/VapiChair';
import {
  buildVapiPromptVariableOverrides,
  buildVapiRoomPromptSystemContent,
  buildVapiTokenPromptSystemContent,
  mergeVapiAssistantOverrides,
} from '../../constants/eventActions';
import { buildVapiAnalysisPlan } from '../../helpers/baglessMemory';

const HOLDER = '.test_vapi_agent_holder';

function t(key, fallback) {
  if (typeof $trans === 'function') {
    const value = $trans(key, {}, fallback || key);
    if (value && typeof value === 'object' && 'text' in value) return value.text || fallback || key;
    return value || fallback || key;
  }
  return fallback || key;
}

function formatCallTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export class VapiAgentTestModal {
  constructor(modeEdit) {
    this.modeEdit = modeEdit;
    this._vapi = null;
    this._callActive = false;
    this._connecting = false;
    this._session = 0;
    this._timerInterval = null;
    this._callStartTime = null;
    this._agent = null;
    this._agents = [];
    this._partialLine = null;
    this._bound = false;
  }

  bind() {
    if (this._bound) return;
    this._bound = true;
    $(document).on('click', `${HOLDER} .modal-background, ${HOLDER} .close-modal`, () => this.close());
    $(document).on('click', '#test_vapi_agent_start', () => this.startCall());
    $(document).on('click', '#test_vapi_agent_end', () => this.endCall());
    $(document).on('change', '#test_vapi_agent_picker', (e) => {
      if (this._callActive || this._connecting) return;
      const idx = parseInt($(e.currentTarget).val(), 10);
      if (!Number.isFinite(idx) || !this._agents[idx]) return;
      const tokenPrompt = this._agent?.tokenPrompt || '';
      const tokenName = this._agent?.tokenName || '';
      this._applyAgent({ ...this._agents[idx], tokenPrompt, tokenName }, { keepTranscript: true });
    });
  }

  open(agent, agents = null) {
    this.bind();
    this.stopCall({ silent: true });
    this._agents = Array.isArray(agents) && agents.length ? agents : (agent ? [agent] : []);
    this._applyAgent(agent || this._agents[0] || null);
    this._renderAgentPicker();
    $('#test_vapi_agent_transcript').empty();
    this._setStatus(t('test-agent-idle', 'Click Start call to talk to this agent'));
    this._setTimer('00:00');
    this._setButtons({ start: true, end: false });
    $(HOLDER).addClass('is-active');
  }

  _applyAgent(agent, { keepTranscript = false } = {}) {
    this._agent = agent || null;
    const name = this._agent?.name || t('vrai-agent-text', 'AI Agent');
    const scenario = this._agent?.scenario || '';
    const image = (this._agent?.image && !String(this._agent.image).includes('empty'))
      ? this._agent.image
      : VapiChair.getDefaultChairImgSrc();
    $('#test_vapi_agent_name').text(name);
    $('#test_vapi_agent_scenario').text(scenario).toggle(!!scenario);
    $('#test_vapi_agent_image').attr('src', image);
    const hasToken = !!(this._agent?.tokenName || this._agent?.tokenPrompt);
    $('#test_vapi_agent_token_row').css('display', hasToken ? 'flex' : 'none');
    $('#test_vapi_agent_token_name').text(this._agent?.tokenName || '');
    const promptPreview = String(this._agent?.tokenPrompt || '').trim();
    $('#test_vapi_agent_token_prompt').text(promptPreview).toggle(!!promptPreview);
    if (!keepTranscript) $('#test_vapi_agent_transcript').empty();
  }

  _renderAgentPicker() {
    const $row = $('#test_vapi_agent_picker_row');
    const $select = $('#test_vapi_agent_picker');
    if (!$select.length || this._agents.length < 2) {
      $row.hide();
      return;
    }
    $select.empty();
    this._agents.forEach((agent, idx) => {
      const label = agent.name || t('vrai-agent-text', 'AI Agent');
      $select.append(`<option value="${idx}">${escapeHtml(label)}</option>`);
    });
    const currentIdx = Math.max(0, this._agents.findIndex((a) => (
      String(a.agentId) === String(this._agent?.agentId) && String(a.libraryId || '') === String(this._agent?.libraryId || '')
    )));
    $select.val(String(currentIdx));
    $row.show();
  }

  close() {
    this.stopCall({ silent: true });
    $(HOLDER).removeClass('is-active');
    this._agent = null;
    this._agents = [];
  }

  async startCall() {
    if (this._callActive || this._connecting || !this._agent) return;
    const agent = this._agent;
    const allowed = await requestAiInteractionConsent(this.modeEdit.model);
    if (!allowed || this._agent !== agent) return;

    const session = ++this._session;
    this._connecting = true;
    this._setButtons({ start: false, end: true });
    this._setStatus(t('vrai-agent-connecting', 'Connecting...'));

    const worldId = String(this.modeEdit.worldID || this.modeEdit.model?.config?.worldId || '').trim();
    const sceneId = String(agent.sceneId || this.modeEdit.currentScene?.id || this.modeEdit.currentSceneId || 'test').trim();
    const agentId = String(agent.agentId || `library-${agent.libraryId || 'test'}`).trim();
    if (!worldId || !sceneId || !agentId) {
      this._connecting = false;
      this._setButtons({ start: true, end: false });
      this._setStatus(t('vrai-agent-no-assistant', 'Agent not configured'));
      return;
    }

    const base = (this.modeEdit.model?.config?.baseWebUrl || window.location?.origin || '').toString().replace(/\/$/, '');
    const tokenUrl = base ? `${base}/vapi-agent/token` : '/vapi-agent/token';
    const body = { worldId, sceneId, agentId };
    if (agent.libraryId) body.overrideLibraryId = agent.libraryId;

    try {
      const res = await fetch(tokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (session !== this._session) return;
      if (!data?.token || !data?.proxyUrl) {
        this._connecting = false;
        this._setButtons({ start: true, end: false });
        this._setStatus(data?.error || t('vrai-agent-connect-failed', 'Connection failed'));
        return;
      }

      const assistantId = (data.assistantId || agent.assistantId || '').trim();
      if (!assistantId) {
        this._connecting = false;
        this._setButtons({ start: true, end: false });
        this._setStatus(t('vrai-agent-no-assistant', 'Agent not configured'));
        return;
      }

      const promptOverrides = mergeVapiAssistantOverrides(
        buildVapiPromptVariableOverrides(agent.roomPrompt, agent.tokenPrompt),
        {
          analysisPlan: buildVapiAnalysisPlan({
            occurredAt: new Date().toISOString(),
            spaceContext: this.modeEdit.model?.sceneData?.name || '',
            roomContext: agent.roomPrompt || '',
          }),
        },
      );
      await releasePreloadedVapiWebRtc();
      const usePublicKeyCall = data.callTransport === 'web';
      const vapi = usePublicKeyCall
        ? new Vapi(data.token, data.proxyUrl)
        : createVapiRCall();
      this._vapi = vapi;
      vapi.on('call-start', () => {
        if (session !== this._session || this._vapi !== vapi) return;
        this._callActive = true;
        this._connecting = false;
        this._startTimer();
        this._setStatus(t('vrai-agent-talk-to-agent-aria', 'Talk to AI'));
        this._sendSystemContext(vapi, buildVapiRoomPromptSystemContent(agent.roomPrompt));
        this._sendSystemContext(vapi, buildVapiTokenPromptSystemContent(agent.tokenPrompt));
      });
      vapi.on('call-end', () => {
        if (session !== this._session || this._vapi !== vapi) return;
        this.stopCall({ ended: true });
      });
      vapi.on('call-start-failed', (err) => {
        if (session !== this._session || this._vapi !== vapi) return;
        this._setStatus(this._formatError(err) || t('vrai-agent-connect-failed', 'Connection failed'));
        this.stopCall({ failed: true });
      });
      vapi.on('error', (err) => {
        if (session !== this._session || this._vapi !== vapi) return;
        this._setStatus(this._formatError(err) || t('vrai-agent-connect-failed', 'Connection failed'));
        this.stopCall({ failed: true });
      });
      vapi.on('speech-start', () => {
        if (session !== this._session || this._vapi !== vapi) return;
        this._setStatus(t('vrai-agent-talking', 'Agent: Talking'));
      });
      vapi.on('speech-end', () => {
        if (session !== this._session || this._vapi !== vapi) return;
        if (this._callActive) this._setStatus(t('vrai-agent-thinking', 'Agent: Thinking'));
      });
      vapi.on('message', (message) => {
        if (session !== this._session || this._vapi !== vapi) return;
        this._handleMessage(message);
      });

      if (usePublicKeyCall) {
        if (agent.isSquad) {
          await vapi.start(undefined, promptOverrides || undefined, assistantId);
        } else {
          await vapi.start(assistantId, promptOverrides || undefined);
        }
      } else if (agent.isSquad) {
        await vapi.start({
          proxyUrl: data.proxyUrl,
          token: data.token,
          squadId: assistantId,
          squadOverrides: promptOverrides || undefined,
        });
      } else {
        await vapi.start({
          proxyUrl: data.proxyUrl,
          token: data.token,
          assistantId,
          assistantOverrides: promptOverrides || undefined,
        });
      }
    } catch (err) {
      if (session !== this._session) return;
      console.warn('[VAPI] test agent failed', err);
      this._connecting = false;
      this._setButtons({ start: true, end: false });
      this._setStatus(this._formatError(err) || t('vrai-agent-connect-failed', 'Connection failed'));
    }
  }

  endCall() {
    this.stopCall();
  }

  stopCall({ silent = false, ended = false, failed = false } = {}) {
    this._session += 1;
    this._connecting = false;
    this._callActive = false;
    this._stopTimer();
    const vapi = this._vapi;
    this._vapi = null;
    if (vapi) {
      try { vapi.stop(); } catch (e) { /* ignore */ }
    }
    this._setButtons({ start: true, end: false });
    if (silent) {
      this._setTimer('00:00');
      return;
    }
    if (failed) return;
    this._setStatus(ended
      ? t('test-agent-idle', 'Click Start call to talk to this agent')
      : t('test-agent-idle', 'Click Start call to talk to this agent'));
  }

  _handleMessage(message) {
    if (!message) return;
    const transcript = typeof message.transcript === 'string' ? message.transcript : '';
    const type = message.type || '';
    if (!transcript && type !== 'transcript') return;
    if (!transcript) return;
    const role = message.role === 'user' ? 'user' : 'agent';
    const isFinal = message.transcriptType ? message.transcriptType === 'final' : true;
    this._appendTranscript(role, transcript, isFinal);
    if (role === 'agent' && !isFinal) {
      this._setStatus(t('vrai-agent-talking', 'Agent: Talking'));
    } else if (role === 'user' && isFinal) {
      this._setStatus(t('vrai-agent-thinking', 'Agent: Thinking'));
    }
  }

  _appendTranscript(role, text, isFinal) {
    const $box = $('#test_vapi_agent_transcript');
    if (!$box.length) return;
    if (this._partialLine && this._partialLine.role === role && !this._partialLine.final) {
      this._partialLine.$el.find('.test-vapi-agent-line-text').text(text);
      this._partialLine.final = isFinal;
      if (isFinal) this._partialLine = null;
      $box.scrollTop($box[0].scrollHeight);
      return;
    }
    const label = role === 'user'
      ? t('vrai-agent-talk-summary-participant-label', 'You')
      : (this._agent?.name || t('vrai-agent-text', 'AI Agent'));
    const $line = $(`
      <div class="test-vapi-agent-line is-${role}">
        <span class="test-vapi-agent-line-role">${escapeHtml(label)}</span>
        <span class="test-vapi-agent-line-text">${escapeHtml(text)}</span>
      </div>
    `);
    $box.append($line);
    if (!isFinal) this._partialLine = { role, final: false, $el: $line };
    else this._partialLine = null;
    $box.scrollTop($box[0].scrollHeight);
  }

  _setStatus(text) {
    $('#test_vapi_agent_status').text(text || '');
  }

  _setTimer(text) {
    $('#test_vapi_agent_timer').text(text || '00:00');
  }

  _setButtons({ start, end }) {
    $('#test_vapi_agent_start').toggle(!!start);
    $('#test_vapi_agent_end').toggle(!!end);
    $('#test_vapi_agent_picker').prop('disabled', !!(!start && end));
  }

  _startTimer() {
    this._stopTimer();
    this._callStartTime = Date.now();
    this._setTimer('00:00');
    this._timerInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - this._callStartTime) / 1000);
      this._setTimer(formatCallTime(elapsed));
    }, 1000);
  }

  _stopTimer() {
    if (this._timerInterval) {
      clearInterval(this._timerInterval);
      this._timerInterval = null;
    }
    this._callStartTime = null;
  }

  _sendSystemContext(vapi, content) {
    if (!content || !vapi) return;
    try {
      vapi.send({
        type: 'add-message',
        message: { role: 'system', content },
        triggerResponseEnabled: false,
      });
    } catch (err) {
      console.warn('[VAPI] test token context inject failed', err);
    }
  }

  _formatError(err) {
    if (err == null) return '';
    if (typeof err === 'string') return err.trim();
    if (typeof err?.message === 'string' && err.message.trim()) return err.message.trim();
    if (typeof err?.error === 'string') return err.error.trim();
    if (err?.error?.message) return String(err.error.message).trim();
    return '';
  }
}
